//import 'moment-duration-format'
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { translate, GET_LIST, useTranslate, useListContext, } from 'react-admin'
import dataProviderFactory from '../../dataProvider'
import { TimeSeries } from 'pondjs'
import styled from 'jss-material-ui'
import { getDateTimeToFullLocalStringLabelFormatter, getHoursMinutesFromTimestamp, getDayMonthFromTimestamp } from '../../utils/date'
import { Paper99White, Title100Chart, H100Grid } from '../../ui/Grid';
import { IconArrowsMaximize, IconArrowsMinimize, IconHelp, IconX } from '@tabler/icons-react';
import { Area, AreaChart, LineChart, Cell, BarChart, Bar, Rectangle, ReferenceArea, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Label } from 'recharts';
import InfoCard from './infoCard'
import { Box, Button, CircularProgress, Paper, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material'
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import StandardDialog from '../../ui/dialog/StandardDialog'
import PaginationMenu from './pagination'

//ticatag




export const P100 = styled('p')({
    root: {
        textAlign: 'right',
        width: '100%',
        marginTop: 2,
        marginBottom: 6,
    },
})


const getUnitSymbol = (unit) => {
    if (unit === null || unit === undefined) {
        return ''
    }
    if (unit === 'percent') return "%"
    if (unit === 'celsius') return "°C"
    if (unit === 'millivolt') return "mV"
    if (unit === 'microgram_per_m3') return "µg/m³"
    if (unit === 'hpa') return "hPa"
    if (unit === 'hectopascal') return "hPa"


    return unit
}




const LoadingCircularProgress = ({ color }) => {
    return (
        <Box sx={{ display: 'flex' }}>
            <CircularProgress color={color} />
        </Box>
    );
}





const CustomizedTick = (props) => {
    const { x, y, stroke, payload } = props;

    const timestamp = payload && payload.value ? payload.value : null;
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} fill="#666">
                <tspan textAnchor="middle" x="0" style={{ fontSize: '12px', fontWeight: 400, lineHeight: '20px' }}>
                    {timestamp ? getHoursMinutesFromTimestamp(timestamp) : ".."}
                </tspan>
                <tspan textAnchor="middle" x="0" dy="20" style={{ fontSize: '10px', fontWeight: 400, lineHeight: '20px' }}>
                    {timestamp ? getDayMonthFromTimestamp(timestamp) : ".."}
                </tspan>

            </text>
        </g>
    );
}

/**
 * Measures chart for events with values in a continues range 
 * 
 */

const MeasuresAreaChart = ({ chartProperties, translate, isSmall }) => {
    const [helpLegendOpen, setHelpLegendOpen] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);

    const defsContent = chartProperties.thresholdPercentFromMax && chartProperties.eventName ? (
        <defs >
            <linearGradient id={`gradient${chartProperties.eventName}`} x1="0" y1="0" x2="0" y2="1" >
                <stop offset={0} stopColor="red" stopOpacity={1} />
                <stop offset={`${chartProperties.thresholdPercentFromMax}%`} stopColor="red" stopOpacity={1} />
                <stop offset={`${chartProperties.thresholdPercentFromMax}%`} stopColor={chartProperties.style.lineColor} stopOpacity={0.8} />
                <stop offset={1} stopColor={chartProperties.style.lineColor} stopOpacity={0} />
            </linearGradient>
        </defs>
    ) : (
        <defs >
            <linearGradient id={`gradient${chartProperties.eventName}`} x1="0" y1="0" x2="0" y2="1" >
                <stop offset="5%" stopColor={chartProperties.style.lineColor} stopOpacity={0.8} />
                <stop offset="95%" stopColor={chartProperties.style.lineColor} stopOpacity={0.05} />
            </linearGradient>
        </defs>
    );


    const handleClickHelpLegend = () => {
        setHelpLegendOpen(!helpLegendOpen);
    };
    const handleClickFullScreen = () => {
        setFullScreen(!fullScreen)
    }

    const ChartHeader = ({ isFullScreen }) => {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'row', margin: "0px 0px 20px 0px" }}>
                <Typography variant='body2' fontWeight="800" pt={"2px"} mb={"2px"} ml={"10px"}> {chartProperties.label}</Typography>
                {chartProperties.unit !== null && chartProperties.unit !== undefined && <Typography variant='caption' fontWeight="400" pt={"2px"} mb={"2px"} ml={"5px"}> {`(${getUnitSymbol(chartProperties.unit)})`}</Typography>}
                <Box sx={{ flex: 1 }} />
                {chartProperties.showHelpLegend === true && <Box mr={"10px"}><IconHelp size={16} cursor={"pointer"} onClick={() => handleClickHelpLegend()} /></Box>}
                {!isSmall && !isFullScreen && <IconArrowsMaximize size={16} cursor={"pointer"} onClick={() => handleClickFullScreen()} />}
                {!isSmall && isFullScreen && <IconArrowsMinimize size={16} cursor={"pointer"} onClick={() => handleClickFullScreen()} />}
            </Box>
        )
    }
    
    const ChartMetrics = () => {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'row', marginBottom: "20px" }}>
                {chartProperties.showDangerThreshold === true &&
                    <InfoCard title={translate('resources.co2.treshold')} value={chartProperties.dangerThreshold + getUnitSymbol(chartProperties.unit)} isSmall={isSmall} />
                }
                <InfoCard title={translate('resources.co2.max')} value={chartProperties.max + getUnitSymbol(chartProperties.unit)} isSmall={isSmall} />
                <InfoCard title={translate('resources.co2.avg')} value={chartProperties.avg + getUnitSymbol(chartProperties.unit)} isSmall={isSmall} />
                <InfoCard title={translate('resources.co2.min')} value={chartProperties.min + getUnitSymbol(chartProperties.unit)} isSmall={isSmall} />
            </Box>
        )
    }

    const ChartResponsiveContainer = () => {
        return (
            <ResponsiveContainer width="100%" minWidth={300} aspect={isSmall ? (1 / 1) : (3 / 1)} minHeight={"140px"} >
                <AreaChart data={chartProperties.series[0].data} width={600} height={300} margin={{ top: 0, right: 0, left: 0, bottom: 80 }} >
                    {defsContent}
                    <ReferenceLine y={chartProperties.dangerThreshold} label={{ value: translate('resources.co2.treshold'), fill: "red", offset: 50, angle: 0, position: 'left' }} stroke="red" strokeDasharray="3 0" alwaysShow={true} />
                    <CartesianGrid strokeWidth={0.5} strokeDasharray='1 0' vertical={false} />
                    <Tooltip labelFormatter={getDateTimeToFullLocalStringLabelFormatter} formatter={(value, name, props) => [value + " " + getUnitSymbol(chartProperties.unit), chartProperties.label]} />
                    <Area type="monotone" name={chartProperties.series[0].name} dataKey="y" stroke={chartProperties.style.lineColor} fill={`url(#gradient${chartProperties.eventName})`} isAnimationActive={false} strokeWidth={3} dot={false} activeDot={{ stroke: chartProperties.style.lineColor, strokeWidth: 1, r: 6 }} />
                    <XAxis dataKey="x" type="number" tickCount={24} angle={-45} tick={CustomizedTick} dy={30} tickLine={false} domain={['dataMin', 'dataMax']} style={{ fontSize: "12px" }} />
                    <YAxis dataKey="y" type="number" tickCount={chartProperties.tickCount ? chartProperties.tickCount : 10} width={40} domain={Array.isArray(chartProperties.yDomain) && chartProperties.yDomain.length === 2 ? chartProperties.yDomain : [0, chartProperties.ymax]} style={{ fontSize: "12px" }} />
                    {
                        Array.isArray(chartProperties.referenceAreas) &&
                        chartProperties.referenceAreas.map((refArea, index) => (
                            <ReferenceArea
                                key={index}
                                x1={chartProperties.series[0].data[0].time}
                                x2={chartProperties.series[0].data[chartProperties.series[0].data.length - 1].time}
                                y1={refArea.yMin}
                                y2={refArea.yMax}
                                fill={refArea.color}
                                fillOpacity={0.2}
                            />
                        ))

                    }
                </AreaChart>
            </ResponsiveContainer>
        )
    }

    const ChartHelpDialog = ({ open }) => {
        return (
            <StandardDialog open={open} BodyComponent={() =>
                <Box
                    sx={{
                        width: {
                            md: "800px",
                            lg: "1000px",
                            xlg: "1300px",
                        }
                    }}>
                    <Box sx={{ width: "100%", height: "100%" }}>
                        <Box sx={{ width: "90%", display: 'flex', flexDirection: "row", justifyContent: "flex-end", padding: "12px 12px 12px 12px" }}><IconX size={24} cursor={"pointer"} onClick={() => handleClickHelpLegend()} > </IconX></Box>
                        {chartProperties.HelpLegendComponent}

                    </Box>
                </Box>
            } />
        )
    }

    const FullScreenDialog = ({ open, onClose }) => {
        return (
            <StandardDialog open={open} onClose={onClose} BodyComponent={() =>
                <Box
                    sx={{
                        width: {
                            md: "800px",
                            lg: "1000px",
                            xlg: "1300px",
                        },
                        height: "60%"
                    }}>
                    <Box sx={{ width: "100%", height: "100%" }}>
                        <PaginationMenu />
                        <ChartHeader isFullScreen={true} />
                        <ChartMetrics />
                        <ChartResponsiveContainer />

                    </Box>
                </Box >
            } />
        )
    }

    const Chart = () => {
        return (
            <Box sx={{ width: "100%", height: "100%" }}>
                <PaginationMenu />
                <ChartHeader />
                <ChartMetrics />
                <ChartHelpDialog open={helpLegendOpen} />
                <FullScreenDialog open={fullScreen} onClose={() => handleClickFullScreen()} />
                <ChartResponsiveContainer />
            </Box>
        )
    }

    return (<Chart />)

}


const MeasuresBarChart = ({ chartProperties, translate, isSmall }) => {
    const [helpLegendOpen, setHelpLegendOpen] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);
    const handleClickHelpLegend = () => {
        setHelpLegendOpen(!helpLegendOpen);
    };
    const handleClickFullScreen = () => {
        setFullScreen(!fullScreen)
    }

    const ChartHeader = ({ isFullScreen }) => {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'row', margin: "0px 0px 20px 0px" }}>
                <Typography variant='body2' fontWeight="800" pt={"2px"} mb={"2px"} ml={"10px"}> {chartProperties.label}</Typography>
                {chartProperties.unit !== null && chartProperties.unit !== undefined && <Typography variant='caption' fontWeight="400" pt={"2px"} mb={"2px"} ml={"5px"}> {`(${getUnitSymbol(chartProperties.unit)})`}</Typography>}
                <Box sx={{ flex: 1 }} />
                {chartProperties.showHelpLegend === true && <Box mr={"10px"}><IconHelp size={16} cursor={"pointer"} onClick={() => handleClickHelpLegend()} /></Box>}
                {!isSmall && !isFullScreen && <IconArrowsMaximize size={16} cursor={"pointer"} onClick={() => handleClickFullScreen()} />}
                {!isSmall && isFullScreen && <IconArrowsMinimize size={16} cursor={"pointer"} onClick={() => handleClickFullScreen()} />}

            </Box>
        )
    }


    const ChartMetrics = () => {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'row', marginBottom: "20px" }}>
                {chartProperties.showDangerThreshold === true &&
                    <InfoCard title={translate('resources.co2.treshold')} value={chartProperties.dangerThreshold + getUnitSymbol(chartProperties.unit)} isSmall={isSmall} />
                }
                <InfoCard title={translate('resources.co2.max')} value={chartProperties.max + getUnitSymbol(chartProperties.unit)} isSmall={isSmall} />
                <InfoCard title={translate('resources.co2.avg')} value={chartProperties.avg + getUnitSymbol(chartProperties.unit)} isSmall={isSmall} />
                <InfoCard title={translate('resources.co2.min')} value={chartProperties.min + getUnitSymbol(chartProperties.unit)} isSmall={isSmall} />
            </Box>
        )
    }

    const ChartResponsiveContainer = () => {
        return (
            <ResponsiveContainer width="100%" minWidth={300} aspect={isSmall ? (1 / 1) : (3 / 1)} minHeight={200} >
                <BarChart data={chartProperties.series[0].data} width={1000} height={300} margin={{ top: 20, right: isSmall ? 5 : 40, left: isSmall ? 0 : 40, bottom: 80 }} >
                    <CartesianGrid strokeWidth={0.5} strokeDasharray='1 0' vertical={false} />
                    <Tooltip labelFormatter={getDateTimeToFullLocalStringLabelFormatter} formatter={(value, name, props) => [value + " " + getUnitSymbol(chartProperties.unit), chartProperties.label]} />
                    <XAxis dataKey="x" type="number" tickCount={24} angle={-45} tick={CustomizedTick} dy={30} tickLine={false} domain={['dataMin', 'dataMax']} style={{ fontSize: "12px" }} />
                    <YAxis dataKey="y" type="number" tickCount={chartProperties.tickCount ? chartProperties.tickCount : 10} width={40} domain={Array.isArray(chartProperties.yDomain) && chartProperties.yDomain.length === 2 ? chartProperties.yDomain : [0, chartProperties.ymax]} style={{ fontSize: "12px" }} />

                    <Bar
                        name={chartProperties.series[0].name}
                        dataKey={`y`}
                        barSize={5}
                        fill="black"
                    >
                        {
                            chartProperties.series[0].data.map((entry, index) => {

                                for (const range of chartProperties.referenceAreas) {
                                    if (entry.y >= range.yMin && entry.y < range.yMax) {
                                        return (
                                            <Cell fill={range.color} key={`cell-${index}`} />
                                        )
                                    }
                                }

                                return (<Cell fill={range.color} key={`cell-${index}`} />)


                            })
                        }

                    </Bar>

                </BarChart>
            </ResponsiveContainer>
        )
    }

    const ChartHelpDialog = ({ open }) => {
        return (
            <StandardDialog open={open} BodyComponent={() =>
                <Box
                    sx={{
                        width: {
                            md: "800px",
                            lg: "1000px",
                            xlg: "1300px",
                        }
                    }}>
                    <Box sx={{ width: "100%", height: "100%" }}>
                        <Box sx={{ width: "90%", display: 'flex', flexDirection: "row", justifyContent: "flex-end", padding: "12px 12px 12px 12px" }}><IconX size={24} cursor={"pointer"} onClick={() => handleClickHelpLegend()} > </IconX></Box>
                        {chartProperties.HelpLegendComponent}

                    </Box>
                </Box>
            } />
        )
    }

    const FullScreenDialog = ({ open, onClose }) => {
        return (
            <StandardDialog open={open} onClose={onClose} BodyComponent={() =>
                <Box
                    sx={{
                        width: {
                            md: "800px",
                            lg: "1000px",
                            xlg: "1300px",
                        },
                        height: "60%"
                    }}>
                    <Box sx={{ width: "100%", height: "100%" }}>
                        <PaginationMenu />
                        <ChartHeader isFullScreen={true} />
                        <ChartMetrics />
                        <ChartResponsiveContainer />

                    </Box>
                </Box >
            } />
        )
    }


    const Chart = () => {
        return (
            <Box sx={{ width: "100%", height: "100%" }}>
                <PaginationMenu />
                <ChartHeader />
                <ChartMetrics />
                <ChartHelpDialog open={helpLegendOpen} />
                <FullScreenDialog open={fullScreen} onClose={() => handleClickFullScreen()} />
                <ChartResponsiveContainer />
            </Box>
        )
    }

    return (<Chart />)
}



// High-G chart: draw two lines (average and peak) in an area chart, and show average/peak/duration in tooltip
const MeasuresHighGChart = ({ chartProperties, translate, isSmall }) => {
    const [helpLegendOpen, setHelpLegendOpen] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);

    // distinct colors for HighGChart only (ignore external style to keep them fixed here)
    const avgColor = '#2e6cc6'
    const peakColor = '#f39c12'

    const defsContent = (
        <defs>
            <linearGradient id={`gradient${chartProperties.eventName}`} x1="0" y1="0" x2="0" y2="1" >
                <stop offset="5%" stopColor={avgColor} stopOpacity={0.8} />
                <stop offset="95%" stopColor={avgColor} stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id={`gradientPeak${chartProperties.eventName}`} x1="0" y1="0" x2="0" y2="1" >
                <stop offset="5%" stopColor={peakColor} stopOpacity={0.6} />
                <stop offset="95%" stopColor={peakColor} stopOpacity={0.05} />
            </linearGradient>
        </defs>
    );

    // X-axis tick uses index, mapped back to timestamp so spacing is uniform regardless of gaps
    const CustomizedIndexTick = (props) => {
        const { x, y, payload } = props;
        const idx = typeof payload?.value === 'number' ? payload.value : null;
        const row = (idx !== null && Array.isArray(chartProperties.dataHighG)) ? chartProperties.dataHighG[idx] : null;
        const timestamp = row ? row.x : null;
        const d = timestamp ? new Date(timestamp) : null;
        const hh = d ? String(d.getHours()).padStart(2, '0') : '..';
        const mm = d ? String(d.getMinutes()).padStart(2, '0') : '..';
        const ss = d ? String(d.getSeconds()).padStart(2, '0') : '..';
        const day = d ? String(d.getDate()).padStart(2, '0') : '..';
        const month = d ? String(d.getMonth() + 1).padStart(2, '0') : '..';
        const isLast = idx !== null && Array.isArray(chartProperties.dataHighG) && idx === chartProperties.dataHighG.length - 1;
        const anchor = isLast ? 'end' : 'middle';
        const dx = isLast ? -5 : 0;
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={dx} y={0} dy={16} fill="#666">
                    <tspan textAnchor={anchor} x={0} style={{ fontSize: '12px', fontWeight: 400, lineHeight: '20px' }}>
                        {`${hh}:${mm}:${ss}`}
                    </tspan>
                    <tspan textAnchor={anchor} x={0} dy="20" style={{ fontSize: '10px', fontWeight: 400, lineHeight: '20px' }}>
                        {`${day}/${month}`}
                    </tspan>
                </text>
            </g>
        );
    }

    const handleClickHelpLegend = () => {
        setHelpLegendOpen(!helpLegendOpen);
    };
    const handleClickFullScreen = () => {
        setFullScreen(!fullScreen)
    }

    const ChartHeader = ({ isFullScreen }) => {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'row', margin: "0px 0px 20px 0px" }}>
                <Typography variant='body2' fontWeight="800" pt={"2px"} mb={"2px"} ml={"10px"}> {chartProperties.label}</Typography>
                {chartProperties.unit !== null && chartProperties.unit !== undefined && <Typography variant='caption' fontWeight="400" pt={"2px"} mb={"2px"} ml={"5px"}> {`(${getUnitSymbol(chartProperties.unit)})`}</Typography>}
                <Box sx={{ flex: 1 }} />
                {chartProperties.showHelpLegend === true && <Box mr={"10px"}><IconHelp size={16} cursor={"pointer"} onClick={() => handleClickHelpLegend()} /></Box>}
                {!isSmall && !isFullScreen && <IconArrowsMaximize size={16} cursor={"pointer"} onClick={() => handleClickFullScreen()} />}
                {!isSmall && isFullScreen && <IconArrowsMinimize size={16} cursor={"pointer"} onClick={() => handleClickFullScreen()} />}
            </Box>
        )
    }

    const CustomHighGTooltip = ({ active, payload }) => {
        if (!active || !payload || payload.length === 0) return null;
        // duration is attached on the first payload's original data
        const duration = payload[0]?.payload?.duration;
        const avgItem = payload.find(p => p.dataKey === 'average');
        const peakItem = payload.find(p => p.dataKey === 'peak');
        const ts = payload[0]?.payload?.x;
        return (
            <Paper sx={{ p: 1 }}>
                <Typography variant="caption">{ts ? getDateTimeToFullLocalStringLabelFormatter(ts) : ''}</Typography>
                <Box>
                    <Typography variant="body2">{translate('resources.high_g.peak')} : {peakItem ? peakItem.value : '-'} G</Typography>
                    <Typography variant="body2">{translate('resources.high_g.average')} : {avgItem ? avgItem.value : '-'} G</Typography>
                    <Typography variant="body2">{translate('resources.high_g.duration')}: {duration !== undefined ? duration : '-'} ms</Typography>
                </Box>
            </Paper>
        )
    }
             
    const ChartResponsiveContainer = () => {
        const LegendHighG = () => (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, mb: 2.45 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 3, bgcolor: avgColor, borderRadius: 1 }} />
                    <Typography variant="caption">{translate('resources.high_g.average')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 3, bgcolor: peakColor, borderRadius: 1 }} />
                    <Typography variant="caption">{translate('resources.high_g.peak')}</Typography>
                </Box>
            </Box>
        );

        return (
            <Box sx={{ width: '100%' }}>
                <LegendHighG />
                <ResponsiveContainer width="100%" minWidth={300} aspect={isSmall ? (1 / 1) : (3 / 1)} minHeight={"140px"} >
                    <AreaChart data={chartProperties.dataHighG} width={600} height={300} margin={{ top: 0, right: 0, left: 0, bottom: 80 }} >
                        {defsContent}
                        <CartesianGrid strokeWidth={0.5} strokeDasharray='1 0' vertical={false} />
                        <Tooltip content={<CustomHighGTooltip />} />
                        <Area connectNulls type="monotone" name={chartProperties.series && chartProperties.series[0] ? chartProperties.series[0].name : 'average'} dataKey="average" stroke={avgColor} fill={`url(#gradient${chartProperties.eventName})`} isAnimationActive={false} strokeWidth={3} dot={{ r: 2 }} activeDot={{ stroke: avgColor, strokeWidth: 1, r: 4 }} />
                        <Area connectNulls type="monotone" name={'peak'} dataKey="peak" stroke={peakColor} fill={`url(#gradientPeak${chartProperties.eventName})`} isAnimationActive={false} strokeWidth={2} dot={{ r: 2 }} activeDot={{ stroke: peakColor, strokeWidth: 1, r: 4 }} />
                        <XAxis dataKey="idx" type="number" ticks={(() => { const n = Array.isArray(chartProperties.dataHighG) ? chartProperties.dataHighG.length : 0; if (n === 0) return []; const desired = isSmall ? 3 : 7; if (n <= desired) return Array.from({ length: n }, (_, i) => i); const step = (n - 1) / (desired - 1); const ticks = []; for (let i = 0; i < desired; i++) { const idx = Math.round(i * step); if (ticks[ticks.length - 1] !== idx) ticks.push(idx);} if (ticks[ticks.length - 1] !== n - 1) ticks[ticks.length - 1] = n - 1; return ticks; })()} angle={-45} tick={CustomizedIndexTick} dy={30} tickLine={false} domain={[0, (chartProperties.dataHighG ? chartProperties.dataHighG.length - 1 : 1)]} style={{ fontSize: "12px" }} />
                        <YAxis dataKey="peak" type="number" tickCount={chartProperties.tickCount ? chartProperties.tickCount : 10} width={40} domain={Array.isArray(chartProperties.yDomain) && chartProperties.yDomain.length === 2 ? chartProperties.yDomain : [0, chartProperties.ymax]} style={{ fontSize: "12px" }} />
                    </AreaChart>
                </ResponsiveContainer>
            </Box>
        )
    }

    // Spacer to keep same overall height as AreaChart metrics block
    const ChartMetricsSpacer = () => (
        <Box sx={{ display: 'flex', flexDirection: 'row', marginBottom: 0, minHeight: 0 }} />
    )

    const FullScreenDialog = ({ open, onClose }) => {
        return (
            <StandardDialog open={open} onClose={onClose} BodyComponent={() =>
                <Box
                    sx={{
                        width: {
                            md: "800px",
                            lg: "1000px",
                            xlg: "1300px",
                        },
                        height: "60%"
                    }}>
                    <Box sx={{ width: "100%", height: "100%" }}>
                        <PaginationMenu />
                        <ChartHeader isFullScreen={true} />
                        <ChartMetricsSpacer />
                        <ChartResponsiveContainer />
                    </Box>
                </Box >
            } />
        )
    }

    const Chart = () => {
        return (
            <Box sx={{ width: "100%", height: "100%" }}>
                <PaginationMenu />
                <ChartHeader />
                <ChartMetricsSpacer />
                <FullScreenDialog open={fullScreen} onClose={() => handleClickFullScreen()} />
                <ChartResponsiveContainer />
            </Box>
        )
    }

    return (<Chart />)
}

const SensorCharts = ({ classes, ...props }) => {

    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const translate = useTranslate();
    const { device, eventName, chartOptions } = props
    const { data, isLoading, isFetching } = useListContext();






    const points = []
    const dataPoints = []
    const chartProperties = { ...chartOptions }




    // no data to display
    if (isLoading) {

        return <LoadingCircularProgress color="primary" />;
    }
    // no data to display
    if (isFetching) {

        return <LoadingCircularProgress color="success" />;
    }
    if (Array.isArray(data) && data.length === 0) {
        return null;
    }
    if ((data === undefined || data === null)) {

        return null;
    }




    // HighGChart has a different payload (average, peak, duration)
    if (chartProperties.type === 'HighGChart') {
        const dataPointsHighG = []
        let maxY = 0
        for (let i = 1; i <= data.length; i += 1) {
            const event = data[data.length - i]
            if (event !== undefined && event !== null) {
                const time = Date.parse(event.timestamp)
                const avgCandidate = event.average ?? event.avg ?? event.mean ?? event.average_value
                const peakCandidate = event.peak ?? event.max ?? event.maximum ?? event.peak_value
                const durationCandidate = event.duration ?? event.duration_ms ?? event.ms
                const avgNum = avgCandidate !== undefined && avgCandidate !== null ? Number(avgCandidate) : NaN
                const peakNum = peakCandidate !== undefined && peakCandidate !== null ? Number(peakCandidate) : NaN
                const avg = isNaN(avgNum) ? null : Math.round(avgNum * 100) / 100
                const peak = isNaN(peakNum) ? null : Math.round(peakNum * 100) / 100
                const duration = durationCandidate
                // only push if we have at least one numeric value
                if (avg !== null || peak !== null) {
                    dataPointsHighG.push({ x: time, idx: dataPointsHighG.length, average: avg, peak: peak, duration: duration })
                    if (typeof peak === 'number' && peak > maxY) maxY = peak
                    if (typeof avg === 'number' && avg > maxY) maxY = avg
                }
            }
        }
        chartProperties.dataHighG = dataPointsHighG
        chartProperties.series = [{ name: eventName }]
        chartProperties.unit = null
        chartProperties.ymax = maxY > 0 ? maxY + (10 - (maxY % 10)) : 10
    } else {
        let filteredData = {}
        // filter units if they are different
        if (chartProperties.unitToShow !== undefined && chartProperties.unitToShow !== null) {
            filteredData = data.filter((ev) => ev.unit === chartProperties.unitToShow);
        } else {
            filteredData = data.filter((ev) => ev.unit === data[0].unit);
        }

        //get time/values for standard charts
        for (let i = 1; i <= filteredData.length; i += 1) {
            const event = filteredData[filteredData.length - i]
            if (event !== undefined && event !== null && eventName === event.name) {
                const time = Date.parse(event.timestamp)
                const value = Math.round(event.value * 100) / 100
                dataPoints.push({ x: time, y: value })
                points.push([time, value])
                //get unit
                chartProperties.unit = event.unit !== undefined ? event.unit : null;
            }
            //temp unit for co2 
            if (event !== undefined && event !== null && event.name === "co2") {
                // no data to display 
                chartProperties.unit = "ppm"
            }
        }

        // Make the TimeSeries here from the points collected above
        const series = new TimeSeries({
            name: eventName,
            columns: ['time', eventName],
            points: points,
        })

        chartProperties.series = [{
            name: eventName,
            data: dataPoints
        }];

        // calculate statistics
        if (points.length > 0) {
            chartProperties.avg = parseInt(series.avg(eventName), 10)
            chartProperties.max = parseInt(series.max(eventName), 10)
            chartProperties.min = parseInt(series.min(eventName), 10)
            chartProperties.maxTime = series.range() ? series.range().end() : 0
            chartProperties.minTime = series.range() ? series.range().begin() : 0
            chartProperties.ymax = chartProperties.max + (10 - (chartProperties.max % 10))
        } else {
            chartProperties.show = false;
        }

        // add threshold 
        if (chartProperties.showDangerThreshold === true) {
            if (device != null && device.configuration) {
                chartProperties.dangerThreshold = device.configuration.co2_alert_threshold
                chartProperties.thresholdPercentFromMax = ((chartProperties.max - chartProperties.dangerThreshold) / chartProperties.max) * 100;
            } else {
                chartProperties.showDangerThreshold = false
                chartProperties.dangerThreshold = null
            }
        }
    }

    return (
        <Paper elevation={0} sx={{
            backgroundColor: `${theme.palette.background.paper} !important`,
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            margin: 0,
            borderRadius: "12px",
            padding: "5px 20px 20px 20px",
            width: '100%',
            minHeight: '300px',
            maxHeight: '450px'
        }}>

            <div id="chartsToExport" >
                {chartProperties.type === "AreaChart" && <MeasuresAreaChart chartProperties={chartProperties} translate={translate} isSmall={isSmall} />}
                {chartProperties.type === "BarChart" && <MeasuresBarChart chartProperties={chartProperties} translate={translate} isSmall={isSmall} />}
                {chartProperties.type === "HighGChart" && <MeasuresHighGChart chartProperties={chartProperties} translate={translate} isSmall={isSmall} />}
            </div >

        </Paper>


    )


}
export default SensorCharts;


